import { useCallback, useRef, useState } from "react";
import { ArrowRight, FileText, Loader2, Paperclip, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiService } from "@/lib/api-service";
import {
	trackInterviewAttachmentUploaded,
	trackInterviewAttachmentsContinued,
} from "@/lib/analytics-events";

interface UploadedFile {
	id: number;
	filename: string;
	file_size_bytes: number;
}

/**
 * Attachment upload screen shown after interview completion.
 * Candidates can upload supporting documents (PDFs, images, Word docs)
 * before proceeding to the final thank-you screen.
 */
export function InterviewAttachments({
	campaignSlug,
	onContinue,
}: {
	campaignSlug: string;
	onContinue: () => void;
}) {
	const [files, setFiles] = useState<UploadedFile[]>([]);
	const [isUploading, setIsUploading] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const handleUpload = useCallback(
		async (fileList: FileList | File[]) => {
			setError(null);
			setIsUploading(true);
			try {
				for (const file of Array.from(fileList)) {
					const result = await apiService.uploadInterviewAttachment(
						campaignSlug,
						file,
					);
					const ext = file.name.split(".").pop()?.toLowerCase() || "unknown";
					trackInterviewAttachmentUploaded(ext, file.size);
					setFiles((prev) => [
						...prev,
						{
							id: result.id,
							filename: result.filename,
							file_size_bytes: result.file_size_bytes,
						},
					]);
				}
			} catch (err) {
				setError(
					err instanceof Error ? err.message : "Upload failed. Please try again.",
				);
			} finally {
				setIsUploading(false);
			}
		},
		[campaignSlug],
	);

	const handleDelete = useCallback(
		async (id: number) => {
			try {
				await apiService.deleteInterviewAttachment(campaignSlug, id);
				setFiles((prev) => prev.filter((f) => f.id !== id));
			} catch (err) {
				setError(
					err instanceof Error ? err.message : "Delete failed.",
				);
			}
		},
		[campaignSlug],
	);

	const onDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);
			if (e.dataTransfer.files.length > 0) {
				handleUpload(e.dataTransfer.files);
			}
		},
		[handleUpload],
	);

	const formatSize = (bytes: number) => {
		if (bytes < 1024) return `${bytes} B`;
		if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	};

	return (
		<div className="h-full flex flex-col items-center justify-center px-6">
			<div className="w-full max-w-lg space-y-6">
				{/* Header */}
				<div className="text-center space-y-2">
					<div className="flex items-center justify-center">
						<div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
							<Paperclip className="h-7 w-7 text-primary" />
						</div>
					</div>
					<h2 className="text-2xl font-semibold text-foreground">
						Upload Documents
					</h2>
					<p className="text-muted-foreground text-sm">
						Attach any supporting documents for your campaign - platform
						statements, endorsement letters, press coverage, or anything that
						helps tell your story. This step is optional.
					</p>
				</div>

				{/* Drop zone */}
				<div
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragging(true);
					}}
					onDragLeave={() => setIsDragging(false)}
					onDrop={onDrop}
					onClick={() => inputRef.current?.click()}
					className={`
						border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
						transition-colors duration-150
						${isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}
					`}
				>
					<input
						ref={inputRef}
						type="file"
						multiple
						accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
						className="hidden"
						onChange={(e) => {
							if (e.target.files?.length) {
								handleUpload(e.target.files);
								e.target.value = "";
							}
						}}
					/>
					{isUploading ? (
						<div className="space-y-2">
							<Loader2 className="h-8 w-8 text-primary mx-auto animate-spin" />
							<p className="text-sm text-muted-foreground">Uploading...</p>
						</div>
					) : (
						<div className="space-y-2">
							<Upload className="h-8 w-8 text-muted-foreground mx-auto" />
							<p className="text-sm font-medium text-foreground">
								Drop files here or click to browse
							</p>
							<p className="text-xs text-muted-foreground">
								PDF, DOC, DOCX, TXT, PNG, JPG - up to 25 MB each
							</p>
						</div>
					)}
				</div>

				{/* Error */}
				{error && (
					<div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-center">
						<p className="text-sm text-destructive">{error}</p>
					</div>
				)}

				{/* File list */}
				{files.length > 0 && (
					<div className="space-y-2">
						{files.map((f) => (
							<div
								key={f.id}
								className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-3"
							>
								<div className="flex items-center gap-3 min-w-0">
									<FileText className="h-4 w-4 text-muted-foreground shrink-0" />
									<div className="min-w-0">
										<p className="text-sm font-medium truncate">{f.filename}</p>
										<p className="text-xs text-muted-foreground">
											{formatSize(f.file_size_bytes)}
										</p>
									</div>
								</div>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => handleDelete(f.id)}
									className="text-muted-foreground hover:text-destructive shrink-0"
								>
									<Trash2 className="h-4 w-4" />
								</Button>
							</div>
						))}
					</div>
				)}

				{/* Actions */}
				<div className="flex flex-col gap-3">
					<Button
						size="lg"
						onClick={() => {
						trackInterviewAttachmentsContinued(files.length);
						onContinue();
					}}
						className="w-full"
					>
						{files.length > 0 ? "Continue" : "Skip & Continue"}
						<ArrowRight className="ml-2 h-4 w-4" />
					</Button>
				</div>
			</div>
		</div>
	);
}
